import React, { useEffect, useState } from "react";
import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { DataGrid, GridActionsCellItem, GridColDef, GridSlots, GridToolbarContainer } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { Alert, AlertProps, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, InputLabel, MenuItem, Select, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { appConfig } from "../appConfig";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AppRole, getAppRole } from "../utils/AppRole";
import { loginRequest } from "../authConfig";
import { FormOneBStatus } from "../models/FormOneBStatus";
import AddIcon from "@mui/icons-material/Add";
import PreviewIcon from "@mui/icons-material/Preview";
import ArchiveIcon from '@mui/icons-material/Archive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InputMask from "react-input-mask";
import { DatePicker } from "@mui/x-date-pickers";
import { v4 as uuid } from "uuid";
import { PoolingAdministrator } from "../components/PoolingAdministrator";
import { PoolingAdministratorInfo } from "../models/Constants";
import CustomDataGridFooter from "../components/CustomDataGridFooter";

interface AddToolbarProps {
    setIsEditMode: (isOpen: boolean) => void;
    setRowEditId: (id: string) => void;
}

// Match AdminFormOneB: number of days after today allowed for date selection
const effectiveDateAddDays = 5;

function AddToolbar(props: AddToolbarProps) {
    const { setRowEditId, setIsEditMode } = props;
    const handleClick = () => {
        setIsEditMode(true);
        setRowEditId("");
    }

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add
            </Button>
        </GridToolbarContainer>
    );
};

export function AdminFormFive() {
    const initialRequest = {
        ...PoolingAdministratorInfo,
        blocks: [""],
    };

    const columns: GridColDef<(typeof rows)[number]>[] = [
        {
            field: "referenceNumber",
            headerName: "Ref #",
            width: 150,
        },
        {
            field: "entityName",
            headerName: "Carrier",
            width: 250,
        },
        {
            field: "blockRange",
            headerName: "Block Range",
            width: 250,
        },
        {
            field: "createdAt",
            headerName: "Created At",
            type: "date",
            width: 200,
            valueFormatter: (value) =>
                dayjs(value as string).format('YYYY-MM-DD hh:mm A')
        },
        {
            field: "displayFormOneBStatus",
            headerName: "Status",
            width: 150
        },
        {
            field: "actions",
            type: "actions",
            headerName: "",
            width: 160,
            cellClassName: "actions",
            getActions: ({ id }) => {
                const r = rows.find(x => x.id === id);
                return [
                    <Tooltip title="View">
                        <GridActionsCellItem
                            icon={<PreviewIcon />}
                            label="View"
                            className="textPrimary"
                            onClick={handlePreviewClick(id.toString())}
                            color="inherit"
                        /></Tooltip>,
                    <Tooltip title="Archive">
                        <GridActionsCellItem
                            icon={<ArchiveIcon />}
                            label="Archive"
                            className="textPrimary"
                            onClick={handleArchiveClick(id.toString())}
                            color="inherit"
                            disabled={r && (r.formOneBStatus == FormOneBStatus.Archived || r.formOneBStatus == FormOneBStatus.Received)}
                        /></Tooltip>,
                    <Tooltip title="Mark Successful">
                        <GridActionsCellItem
                            icon={<CheckCircleIcon />}
                            label="Mark successful"
                            onClick={handleSuccessClick(id.toString())}
                            className="textPrimary"
                            color="inherit"
                            disabled={r && r.formOneBStatus != FormOneBStatus.Working}
                        /></Tooltip>,
                    <Tooltip title="Mark Failed">
                        <GridActionsCellItem
                            icon={<ErrorIcon />}
                            label="Mark Failed"
                            onClick={handleFailedClick(id.toString())}
                            className="textPrimary"
                            color="inherit"
                            disabled={r && r.formOneBStatus != FormOneBStatus.Working}
                        /></Tooltip>
                ];
            },
        },
    ];

    const { instance } = useMsal();
    const [rows, setRows] = useState<any>([]);
    const [accessToken, setAccessToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [rowEditId, setRowEditId] = useState("");
    const [formRequest, setFormRequest] = useState(initialRequest);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [failedOpen, setFailedOpen] = useState(false);
    const [carriers, setCarriers] = useState([]);
    const [selectedCarrierId, setSelectedCarrierId] = useState("");
    const [selectedCodeHolderProvince, setSelectedCodeHolderProvince] = useState("");

    const handleCloseDialog = () => {
        setIsEditMode(false);
        setFormRequest(initialRequest);
        setSelectedCodeHolderProvince("");
        setSelectedCarrierId("");
    }

    const handleArchiveClick = (id: string) => () => {
        setRowEditId(id);
        setArchiveOpen(true);
    }

    const handleSuccessClick = (id: string) => () => {
        setRowEditId(id);
        setSuccessOpen(true);
    }

    const handleFailedClick = (id: string) => () => {
        setRowEditId(id);
        setFailedOpen(true);
    }

    const handleArchiveCancel = () => {
        setArchiveOpen(false);
    }

    const handleSuccessCancel = () => {
        setSuccessOpen(false);
    }
    const handleFailedCancel = () => {
        setFailedOpen(false);
    }

    const handleArchiveOk = () => {
        const url = `${appConfig.api.url}/api/formfive/${rowEditId}/archive`;
        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => response.json())
            .then((response) => {
                setArchiveOpen(false);
                const newRows = rows.map(r => r.id === rowEditId ? { ...r, formOneBStatus: FormOneBStatus.Archived } : r);
                setRows(newRows);
            });
    }

    const handleSuccessOk = () => {
        const url = `${appConfig.api.url}/api/formfive/${rowEditId}/success`;
        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => response.json())
            .then((response) => {
                setSuccessOpen(false);
                const newRows = rows.map(r => r.id === rowEditId ? { ...r, formOneBStatus: FormOneBStatus.Success } : r);
                setRows(newRows);
            });
    }

    const handleFailedOk = () => {
        const url = `${appConfig.api.url}/api/formfive/${rowEditId}/failed`;
        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => response.json())
            .then((response) => {
                setFailedOpen(false);
                const newRows = rows.map(r => r.id === rowEditId ? { ...r, formOneBStatus: FormOneBStatus.Error } : r);
                setRows(newRows);
            });
    }

    const handleReclamationDateChange = (value) => {
        setFormRequest(props => { return { ...props, ["blockHolderReclamationDate"]: value } });
    }

    const handlePreviewClick = (id: string) => () => {
        setIsEditMode(true);
        setRowEditId(id);

        const url = `${appConfig.api.url}/api/formfive/${id}`;
        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => response.json())
            .then((response) => {
                setFormRequest(response);
                setSelectedCodeHolderProvince(response.codeHolderProvince);
                selectCarrier(response.carrierId);
            });
    };

    const selectCarrier = (id: any) => {
        const carrier = carriers.find((c: any) => c.id === id);
        const carrierName = carrier ? carrier["name"] : "N/A";
        setFormRequest(props => {
            return { ...props, ["carrierId"]: id, "entityName": carrierName, "contactName": carrierName, "carrierName": carrierName }
        });
        setSelectedCarrierId(id);
    }

    const handleUpdate = (name, value, isCheckbox = false, isProvince = false) => {
        setFormRequest(props => { return { ...props, [name]: isCheckbox ? value === "on" : value } });
        if (isProvince) {
            setSelectedCodeHolderProvince(value);
        }
    }

    const [snackbar, setSnackbar] = React.useState<Pick<
        AlertProps,
        "children" | "severity"
    > | null>(null);

    const handleCloseSnackbar = () => setSnackbar(null);

    useEffect(() => {
        setLoading(true);
        instance
            .acquireTokenSilent(loginRequest)
            .then(function (accessTokenResponse) {
                // Acquire token interactive success
                setAccessToken(accessTokenResponse.accessToken);
                // check Role
                const role = getAppRole(accessTokenResponse.accessToken);
                if (role !== AppRole.Administrator) {
                    instance.logoutRedirect();
                }

                fetch(`${appConfig.api.url}/api/formfive`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessTokenResponse.accessToken}`,
                    },
                })
                    .then((response) => response.json())
                    .then((response) => {
                        setRows(response);
                        setLoading(false);
                    });

                if (carriers && carriers.length == 0) {
                    fetch(`${appConfig.api.url}/api/carriers`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${accessTokenResponse.accessToken}`,
                        },
                    })
                        .then((response) => response.json())
                        .then((response) => {
                            setCarriers(response);
                        });
                }
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    }, [instance]);

    return (
        <>
            <AuthenticatedTemplate>
                <Box sx={{ height: 700, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                            sorting: {
                                sortModel: [{ field: 'createdAt', sort: 'desc' }]
                            }
                        }}
                        pageSizeOptions={[10]}
                        disableRowSelectionOnClick
                        loading={loading}
                        getRowClassName={(params) => `row-${params.row.formOneBStatus}`}
                        slots={{
                            toolbar: AddToolbar as GridSlots["toolbar"],
                            footer: CustomDataGridFooter as GridSlots["footer"],
                        }}
                        slotProps={{
                            toolbar: { setRowEditId, setIsEditMode },
                        }}
                    />
                </Box>
                <Dialog
                    open={isEditMode}
                    onClose={handleCloseDialog}
                    className="form dialog"
                    PaperProps={{
                        component: 'form',
                        onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                            event.preventDefault();

                            const input = document.querySelector(
                                `input[aria-invalid="true"]`
                            ) as HTMLElement;

                            if (input) {
                                input.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                    inline: 'start',
                                });
                                input.focus();
                                return;
                            }

                            let data = formRequest;

                            // Mirror AdminFormOneB behaviour: if no date selected, default to today + 5 days
                            if (!formRequest["blockHolderReclamationDate"]) {
                                data["blockHolderReclamationDate"] = dayjs(new Date()).add(effectiveDateAddDays, 'day');
                            }

                            const url = rowEditId ?
                                `${appConfig.api.url}/api/formfive/${rowEditId}` :
                                `${appConfig.api.url}/api/formfive`;
                            const method = rowEditId ? "PUT" : "POST";
                            if (!rowEditId) {
                                data["id"] = uuid();
                            }

                            fetch(url, {
                                method: method,
                                headers: {
                                    Authorization: `Bearer ${accessToken}`,
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(data),
                            })
                                .then((response) => response.json())
                                .then((response) => {
                                    if (response.referenceNumber) {
                                        setIsEditMode(false);

                                        // If adding new carrier add new row to the grid 
                                        if (!rowEditId) {
                                            setRows((oldRows) => [
                                                ...oldRows,
                                                {
                                                    id: formRequest["id"],
                                                    referenceNumber: response.referenceNumber,
                                                    name: formRequest["name"],
                                                    groupId: formRequest["groupId"],
                                                    entityName: formRequest["entityName"],
                                                    code: formRequest["code"],
                                                    blockRange: formRequest["blockRange"],
                                                    formOneBStatus: 0,
                                                    createdAt: new Date(),
                                                },
                                            ]);
                                        }
                                        setFormRequest(initialRequest);
                                        setSelectedCodeHolderProvince("");
                                        setSelectedCarrierId("");
                                        setSnackbar({
                                            children: "Request successfully saved",
                                            severity: "success",
                                        });
                                    } else {
                                        setSnackbar({ children: "Failed to save data", severity: "error" });
                                    }
                                })
                                .catch((error) => {
                                    setSnackbar({ children: error.message, severity: "error" });
                                });
                        },
                    }}
                >
                    <DialogContent>
                        {/* Section A */}
                        <Typography variant="h6">Section A</Typography>
                        <Grid container spacing={1} className="container">
                            <PoolingAdministrator></PoolingAdministrator>
                            <Grid item xs={12}>
                                <InputLabel>Current Block Holder NPAC SOA SPID</InputLabel>
                                <TextField
                                    required
                                    error={formRequest["spid"] && !/^[A-Za-z0-9]{4}$/.test(formRequest["spid"])}
                                    name="spid"
                                    value={formRequest["spid"]}
                                    disabled={(rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued) ? true : false}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                    inputProps={{ maxLength: 4 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel>Thousands-Block to be Reclaimed</InputLabel>
                                <InputMask
                                    mask="999-999-9" maskChar="x"
                                    error={formRequest["blockRange"] && !/^\d{7}$/.test(formRequest["blockRange"])}
                                    inputProps={{ maxLength: 7 }}
                                    value={formRequest["blockRange"]}
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                >
                                    {() => (
                                        rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? (
                                            <TextField
                                                name="blockRange"
                                                fullWidth
                                                disabled
                                                required
                                            />) : (
                                            <TextField
                                                name="blockRange"
                                                fullWidth
                                                required
                                            />
                                        )
                                    )}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel>Block Holder Reclamation Date</InputLabel>
                                {rowEditId ?
                                    (<DatePicker
                                        name="blockHolderReclamationDate"
                                        disabled={formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                        onChange={(e) => handleReclamationDateChange(e)}
                                        minDate={dayjs(new Date()).add(effectiveDateAddDays, 'day')}
                                        value={dayjs(formRequest["blockHolderReclamationDate"])}
                                    />) : (
                                        <DatePicker
                                            name="blockHolderReclamationDate"
                                            onChange={(e) => handleReclamationDateChange(e)}
                                            minDate={dayjs(new Date()).add(effectiveDateAddDays, 'day')}
                                            defaultValue={dayjs(new Date()).add(effectiveDateAddDays, 'day')}
                                        />)}
                            </Grid>
                        </Grid>
                        {/* Section B */}
                        <Typography variant="h6">Section B</Typography>
                        <Grid container spacing={1} className="container">
                            <Grid item xs={12}>
                                <InputLabel>Code Holder:</InputLabel>
                                <Select
                                    required
                                    name="carrierId"
                                    disabled={rowEditId ? true : false}
                                    value={selectedCarrierId}
                                    onChange={(e) => selectCarrier(e.target.value)}
                                    fullWidth
                                >
                                    {carriers.map((c: any) => (
                                        <MenuItem value={c.id} key={c.name}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>
                        {/* Section C */}
                        {rowEditId && (
                            <>
                                <Typography variant="h6">Section C</Typography>
                                <Grid container spacing={1} className="container">
                                    <Grid item xs={12}>
                                        <InputLabel>Status:</InputLabel> {FormOneBStatus[formRequest["formOneBStatus"]]}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Thousands-Block Reclamation Complete:</InputLabel>
                                        {formRequest["completionDate"] ? "Yes" : "No"}
                                    </Grid>
                                    <Grid item xs={8}>
                                        <InputLabel>Complete Date:</InputLabel> {formRequest["completionDate"] ? dayjs(formRequest["completionDate"]).format("YYYY-MM-DD hh:mm A") : ""}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Code Holder Contacted:</InputLabel>
                                        {formRequest["codeHolderContactDate"] ? "Yes" : "No"}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Contact Date:</InputLabel> {formRequest["codeHolderContactDate"] ? dayjs(formRequest["codeHolderContactDate"]).format("YYYY-MM-DD hh:mm A") : ""}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Contact Name:</InputLabel> {formRequest["codeHolderContactedName"]}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Block Holder Contacted:</InputLabel>
                                        {formRequest["blockHolderContactDate"] ? "Yes" : "No"}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Contact Date:</InputLabel> {formRequest["blockHolderContactDate"] ? dayjs(formRequest["blockHolderContactDate"]).format("YYYY-MM-DD hh:mm A") : ""}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Contact Name:</InputLabel> {formRequest["blockHolderContactedName"]}
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        {rowEditId && rowEditId != "" && formRequest["formOneBStatus"] != FormOneBStatus.Received &&
                            formRequest["formOneBStatus"] != FormOneBStatus.Archived &&
                            (
                                <Button onClick={handleArchiveClick(rowEditId)}>Archive</Button>
                            )
                        }
                        {(!rowEditId || rowEditId == "" || formRequest["formOneBStatus"] == FormOneBStatus.Queued) &&
                            (
                                <Button type="submit">Save</Button>
                            )}
                    </DialogActions>
                </Dialog>
                <Dialog
                    sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
                    maxWidth="xs"
                    open={archiveOpen}
                >
                    <DialogTitle>Confirmation required</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to archive this request?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleArchiveOk}>Ok</Button>
                        <Button autoFocus onClick={handleArchiveCancel}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
                    maxWidth="xs"
                    open={successOpen}
                >
                    <DialogTitle>Confirmation required</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to mark this request as <b style={{ color: "green" }}>Successful</b>?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleSuccessOk}>Ok</Button>
                        <Button autoFocus onClick={handleSuccessCancel}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
                    maxWidth="xs"
                    open={failedOpen}
                >
                    <DialogTitle>Confirmation required</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to mark this request as <b style={{ color: "red" }}>Failed</b>?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleFailedOk}>Ok</Button>
                        <Button autoFocus onClick={handleFailedCancel}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
                {!!snackbar && (
                    <Snackbar
                        open
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        onClose={handleCloseSnackbar}
                        autoHideDuration={6000}
                    >
                        <Alert {...snackbar} onClose={handleCloseSnackbar} />
                    </Snackbar>
                )}
            </AuthenticatedTemplate>
        </>
    )
}
